import styled from 'styled-components';
import { ActivityStatus } from '../types';
import { COLORS } from './constants';

// Base Container Components
export const Wrapper = styled.div`
  padding: 20px;
  width: 100%;
  margin: 0 auto;
`;

export const ActivityMenuContainer = styled.div`
  width: 100%;
  max-width: 400px;
  background: ${COLORS.BACKGROUND_PRIMARY};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

// List Components
export const ActivityList = styled.div<{
  $maxHeight?: string;
  $isInfinite?: boolean;
  $view?: string;
}>`
  ${({ $view, $maxHeight, $isInfinite }) =>
    $view === 'fullPage'
      ? `
        max-height: none;
        overflow-y: visible;
        height: auto;
      `
      : `
        max-height: ${
          $isInfinite ||
          !$maxHeight ||
          $maxHeight === 'endless' ||
          $maxHeight === 'infinite'
            ? '100vh'
            : $maxHeight
        };
        overflow-y: auto;
        height: ${$isInfinite ? '100%' : 'auto'};
      `}

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${COLORS.BACKGROUND};
  }
  &::-webkit-scrollbar-thumb {
    background: ${COLORS.BORDER_LIGHT};
    border-radius: 3px;
  }
`;

// Activity Item Components
export const ActivityItemWrapper = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${COLORS.BORDER_LIGHT};
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    background: ${COLORS.BACKGROUND};
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const ActivityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

export const ActivityTitle = styled.h4`
  margin: 0;
  color: ${COLORS.TEXT_PRIMARY};
  font-size: 1rem;
  font-weight: 700;
`;

export const ActivityTimestamp = styled.span`
  color: ${COLORS.TEXT_SECONDARY};
  font-size: 0.9rem;
`;

export const ActivityDescription = styled.p`
  margin: 0;
  color: ${COLORS.TEXT_SECONDARY};
  font-size: 0.9rem;
`;

export const ActivityMetadata = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${COLORS.TEXT_SECONDARY};

  span {
    &:not(:first-child) {
      margin-left: 8px;
    }
  }
`;

export const ActivityStatusBadge = styled.span<{
  $status: ActivityStatus;
  $color: string;
}>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-top: 8px;
  display: inline-block;
  background: ${({ $color }) => `${$color}20`};
  color: ${({ $color }) => $color};
`;

// View Components
export const FullPageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

export const SidePopoutContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 400px;
  background: ${COLORS.BACKGROUND_PRIMARY};
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow-y: auto;
  transform: translateX(0);
  transition: transform 0.3s ease-in-out;

  &.closing {
    transform: translateX(100%);
  }
`;

// Header Components
export const ViewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid ${COLORS.BORDER_LIGHT};
  position: sticky;
  top: 0;
  background: ${COLORS.BACKGROUND_PRIMARY};
  z-index: 1;
`;

export const ViewTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  color: ${COLORS.TEXT_PRIMARY};
`;

// Button Components
export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: ${COLORS.TEXT_SECONDARY};
  transition: color 0.2s;

  &:hover {
    color: ${COLORS.TEXT_PRIMARY};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ViewEmailButton = styled.button`
  background: none;
  border: none;
  color: ${COLORS.PRIMARY};
  font-size: 12px;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${COLORS.PRIMARY}10;
  }
`;

// Filter Components
export const PillBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const Pill = styled.button<{ $active: boolean }>`
  padding: 6px 16px;
  border-radius: 16px;
  border: 1px solid ${COLORS.BORDER_LIGHT};
  background: ${({ $active }) =>
    $active ? COLORS.PRIMARY : COLORS.BACKGROUND};
  color: ${({ $active }) => ($active ? COLORS.BACKGROUND_PRIMARY : COLORS.TEXT_PRIMARY)};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${COLORS.PRIMARY};
    color: ${COLORS.BACKGROUND_PRIMARY};
  }
`;

// State Components
export const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${COLORS.TEXT_SECONDARY};
`;

export const LoadingMoreIndicator = styled.div`
  padding: 16px;
  text-align: center;
  color: ${COLORS.TEXT_SECONDARY};
  border-top: 1px solid ${COLORS.BORDER_LIGHT};
`;
